import { useState } from 'react';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import {
  Button,
  Caption,
  Card,
  CardHead,
  ErrorState,
  Field,
  Loading,
  Notice,
  Page,
  PageHead,
  Tag,
  TextInput,
} from '@/components/ui';
import { SignOutButton } from '@/components/SignOutButton';
import { formatDate } from '@/lib/format';

/**
 * Agency onboarding: the two things that must be recorded before this agency
 * can be served (launch gates 4 and 9).
 *
 * Both were enforceable before this screen existed and neither was reachable:
 * the API refused every request with `policy_not_approved`, and the only way
 * through was an administrator calling the endpoints by hand. That made the
 * gates provable and the product unusable without a developer — and in the case
 * of the BAA it meant the record of who agreed said, in effect, "whoever had a
 * shell".
 *
 * Both routes sit outside their own gates on purpose; a remedy behind the gate
 * it clears is a deadlock.
 */

type PolicyFull = {
  family_update_approval_mode: string;
  rating_edit_window_hours: number;
  escalation_types: string[];
  access_window_minutes_before: number;
  access_grace_cutoff_local: string;
  quiet_hours: { start: string; end: string };
  audio_retention_enabled: boolean;
  pilot_envelope: Record<string, unknown>;
  emergency_copy: string;
  approved_at: string | null;
};

type BaaStatus = {
  current_version: string;
  document_url: string | null;
  accepted_version: string | null;
  accepted_at: string | null;
  is_current: boolean;
};

export function OnboardingPage({ onComplete }: { onComplete: () => void }) {
  const policy = useApi<PolicyFull>('/policy/full');
  const baa = useApi<BaaStatus>('/policy/baa');

  if (policy.loading || baa.loading) return <Loading label="Checking what is outstanding" />;
  if (policy.error) return <Page><ErrorState message={policy.error.message} onRetry={policy.reload} /></Page>;
  if (baa.error) return <Page><ErrorState message={baa.error.message} onRetry={baa.reload} /></Page>;
  if (!policy.data || !baa.data) return null;

  const policyDone = policy.data.approved_at !== null;
  const baaDone = baa.data.is_current;
  const allDone = policyDone && baaDone;

  return (
    <Page>
      <PageHead
        title="Before this agency can be served"
        subtitle="Two confirmations. Both are recorded, and both can be shown to a reviewer."
      />

      {allDone ? (
        <Notice tone="positive" title="Setup is complete">
          The operating policy is approved and the Business Associate Agreement is on file.
          <div style={{ marginTop: 16 }}>
            <Button onClick={onComplete}>Go to the console</Button>
          </div>
        </Notice>
      ) : (
        <Notice tone="attention" title="The service is refusing requests for this agency">
          That is deliberate, not a fault. Until both steps below are recorded, every
          request is refused — so no family sees a plan, and no caregiver writes a note,
          under rules nobody has agreed to.
        </Notice>
      )}

      <PolicyStep policy={policy.data} done={policyDone} onDone={policy.reload} />
      <BaaStep status={baa.data} done={baaDone} onDone={baa.reload} />

      {/* Onboarding replaces the shell, so without this there is no way out of
          the console at all — and both steps below tell people to sign out and
          back in when their authentication is too old to approve. */}
      <div className="onboarding__footer">
        <SignOutButton subtle />
      </div>
    </Page>
  );
}

// ------------------------------------------------------------- step one
function PolicyStep({
  policy,
  done,
  onDone,
}: {
  policy: PolicyFull;
  done: boolean;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function approve() {
    setProblem(null);
    setBusy(true);
    try {
      await api.post('/policy/approve', {});
      onDone();
    } catch (caught) {
      const error = caught as ApiError;
      setProblem(
        error.needsReauth
          ? 'For a decision this consequential the server wants a fresh sign-in. Sign out and back in, then approve.'
          : error.message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHead
        title="1. The operating policy"
        actions={done ? <Tag label="Approved" tone="positive" /> : <Tag label="Outstanding" tone="attention" />}
      />
      <Caption>
        These values are in force now. Until they are approved they are placeholders invented
        for a demonstration, not decisions this agency has made.
      </Caption>

      <dl className="policy-list">
        <Row label="Family updates are published">
          {policy.family_update_approval_mode === 'manual'
            ? 'after a coordinator approves each one'
            : policy.family_update_approval_mode}
        </Row>
        <Row label="A family can change a rating for">
          {policy.rating_edit_window_hours} hours
        </Row>
        <Row label="Escalates immediately">
          {policy.escalation_types.join(', ') || 'nothing'}
        </Row>
        <Row label="A caregiver can open the plan">
          {policy.access_window_minutes_before} minutes before the visit, until{' '}
          {policy.access_grace_cutoff_local}
        </Row>
        <Row label="Routine notifications are held">
          {policy.quiet_hours.start} to {policy.quiet_hours.end}
        </Row>
        <Row label="Dictation audio is kept">
          {policy.audio_retention_enabled ? 'yes' : 'no — transcribed on the device, never uploaded'}
        </Row>
        <Row label="Shown on the emergency path">{policy.emergency_copy}</Row>
      </dl>

      {problem ? <Notice tone="urgent" title="Could not approve">{problem}</Notice> : null}

      {done ? (
        <Caption>Approved {formatDate(policy.approved_at as string)}.</Caption>
      ) : (
        <>
          <Caption>
            Changing any value later clears this approval, because a value nobody has looked
            at since it changed is not an approved value.
          </Caption>
          <Button onClick={approve} busy={busy}>
            Approve this policy
          </Button>
        </>
      )}
    </Card>
  );
}

// ------------------------------------------------------------- step two
function BaaStep({
  status,
  done,
  onDone,
}: {
  status: BaaStatus;
  done: boolean;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function accept() {
    setProblem(null);
    setBusy(true);
    try {
      await api.post('/policy/baa/accept', {
        version: status.current_version,
        accepted_by_name: name.trim() || null,
      });
      onDone();
    } catch (caught) {
      const error = caught as ApiError;
      setProblem(
        error.needsReauth
          ? 'This binds the agency to an agreement, so the server wants a fresh sign-in. Sign out and back in, then accept.'
          : error.message,
      );
    } finally {
      setBusy(false);
    }
  }

  const superseded = status.accepted_at !== null && !status.is_current;

  return (
    <Card>
      <CardHead
        title="2. The Business Associate Agreement"
        actions={done ? <Tag label="On file" tone="positive" /> : <Tag label="Outstanding" tone="attention" />}
      />
      <Caption>
        Version {status.current_version}
        {status.document_url ? (
          <>
            {' · '}
            <a href={status.document_url} target="_blank" rel="noreferrer">
              open the published exhibit
            </a>
          </>
        ) : null}
      </Caption>

      {superseded ? (
        <Notice tone="attention" title="A newer version is in force">
          This agency accepted {status.accepted_version}. Version {status.current_version} is
          current, and acceptance does not carry forward — a version nobody has read is not an
          accepted version.
        </Notice>
      ) : null}

      <PlaceholderExhibit version={status.current_version} />

      {problem ? <Notice tone="urgent" title="Could not accept">{problem}</Notice> : null}

      {done ? (
        <Caption>
          Accepted {formatDate(status.accepted_at as string)} — version {status.accepted_version}.
        </Caption>
      ) : (
        <>
          <Field
            label="Your full name"
            hint="Recorded with the acceptance. A reviewer asks who agreed, and a typed name is a better answer than the session that happened to be open."
          >
            {(props) => (
              <TextInput
                {...props}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Angela Foster"
                autoComplete="name"
              />
            )}
          </Field>
          <Button onClick={accept} busy={busy} disabled={!name.trim()}>
            Accept on behalf of this agency
          </Button>
        </>
      )}
    </Card>
  );
}

/**
 * Stands in for the published exhibit until counsel supplies one. It is written
 * to be impossible to mistake for the real thing: the point of this screen is to
 * exercise the flow, and a convincing-looking agreement that nobody has reviewed
 * is worse than an obvious placeholder.
 */
function PlaceholderExhibit({ version }: { version: string }) {
  return (
    <div className="exhibit">
      <Notice tone="urgent" title="Placeholder — not a legal document">
        This text exists so the acceptance flow can be tested. It has not been drafted or
        reviewed by counsel and must be replaced before any agency is onboarded.
      </Notice>
      <div className="exhibit__body">
        <p>
          <strong>Business Associate Exhibit (placeholder)</strong>
        </p>
        <p>
          This exhibit would describe the relationship between the agency, as a covered
          entity, and Djio Holdings Inc, as a business associate processing protected health
          information on the agency&rsquo;s behalf.
        </p>
        <p>A real exhibit has to cover, at minimum:</p>
        <ul>
          <li>permitted uses and disclosures of protected health information</li>
          <li>required safeguards</li>
          <li>flow-down to subcontractors, which is what binds the hosting provider</li>
          <li>breach notification — to whom, within how long</li>
          <li>support for individual access, amendment, and an accounting of disclosures</li>
          <li>return or destruction of the data when the relationship ends</li>
          <li>termination for material breach</li>
        </ul>
        <p>
          None of that is drafted here. Accepting below records that this agency accepted
          version <strong>{version}</strong> at a point in time — which is the artifact a
          vendor review asks for — but the version it records is a placeholder.
        </p>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="policy-list__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
